import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Users, UserPlus, Copy, Check, AlertCircle, Eye,
  TrendingUp, Clock, Target, Award, BookOpen, ChevronRight
} from 'lucide-react';
import { client } from '../api/client';
import { useAuthStore } from '../store/authStore';

interface Student {
  userId: string;
  name: string;
  grade: number | null;
  school: string | null;
  totalPoints: number;
  streakDays: number;
}

interface StudentReport {
  profile: {
    grade: number | null;
    school: string | null;
    totalPoints: number;
    streakDays: number;
  };
  weeklyStats: {
    totalPractice: number;
    correctCount: number;
    accuracy: number;
    avgTimePerQuestion: number;
  };
  mistakeCount: number;
  badgeCount: number;
}

export function ParentPage() {
  const { user } = useAuthStore();
  const [mode, setMode] = useState<'student' | 'parent'>('student');
  const [inviteCode, setInviteCode] = useState('');
  const [inputCode, setInputCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<string | null>(null);
  const [report, setReport] = useState<StudentReport | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (user && mode === 'parent') {
      loadStudents();
    }
  }, [user, mode]);

  async function loadStudents() {
    try {
      const res = await client.api.fetch('/api/parent/students');
      const data = await res.json();
      setStudents(data.data || []);
    } catch (error) {
      console.error('Failed to load students:', error);
    }
  }

  async function generateInviteCode() {
    setLoading(true);
    setError('');
    try {
      const res = await client.api.fetch('/api/parent/invite', { method: 'POST' });
      const data = await res.json();
      if (data.data?.code) {
        setInviteCode(data.data.code);
      }
    } catch (error) {
      setError('生成邀请码失败');
    } finally {
      setLoading(false);
    }
  }

  async function acceptInvite() {
    if (!inputCode.trim()) {
      setError('请输入邀请码');
      return;
    }
    
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await client.api.fetch('/api/parent/accept', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: inputCode }),
      });
      const data = await res.json();
      
      if (res.ok) {
        setSuccess('绑定成功！');
        setInputCode('');
        loadStudents();
      } else {
        setError(data.error || '绑定失败');
      }
    } catch (error) {
      setError('绑定失败');
    } finally {
      setLoading(false);
    }
  }

  async function loadReport(studentId: string) {
    setSelectedStudent(studentId);
    try {
      const res = await client.api.fetch(`/api/parent/report/${studentId}`);
      const data = await res.json();
      setReport(data.data);
    } catch (error) {
      console.error('Failed to load report:', error);
    }
  }

  function copyCode() {
    navigator.clipboard.writeText(inviteCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  if (!user) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-700 mb-2">请先登录</h2>
          <p className="text-slate-500 mb-4">登录后使用家长督学功能</p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            去登录
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-bold text-slate-900 mb-2">家长督学</h1>
          <p className="text-slate-600">绑定学生账号，实时查看学习情况</p>
        </motion.div>

        {/* Mode Toggle */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex gap-2 mb-8 bg-white rounded-xl p-1.5 border border-slate-100 w-fit"
        >
          <button
            onClick={() => setMode('student')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              mode === 'student'
                ? 'bg-slate-900 text-white'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
            }`}
          >
            我是学生
          </button>
          <button
            onClick={() => setMode('parent')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              mode === 'parent'
                ? 'bg-slate-900 text-white'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
            }`}
          >
            我是家长
          </button>
        </motion.div>

        {mode === 'student' ? (
          /* Student View - Generate Invite Code */
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl p-6 border border-slate-100"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                <UserPlus className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <h2 className="font-semibold text-slate-900">邀请家长绑定</h2>
                <p className="text-sm text-slate-500">生成邀请码发送给家长</p>
              </div>
            </div>

            {inviteCode ? (
              <div className="bg-slate-50 rounded-xl p-6 text-center">
                <p className="text-sm text-slate-500 mb-3">您的邀请码（7天有效）</p>
                <div className="flex items-center justify-center gap-3">
                  <span className="text-4xl font-mono font-bold text-slate-900 tracking-widest">
                    {inviteCode}
                  </span>
                  <button
                    onClick={copyCode}
                    className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-200 rounded-lg transition-colors"
                  >
                    {copied ? <Check className="w-5 h-5 text-emerald-500" /> : <Copy className="w-5 h-5" />}
                  </button>
                </div>
                <p className="text-sm text-slate-500 mt-4">
                  请将此邀请码发送给家长，家长使用"我是家长"功能输入邀请码即可绑定
                </p>
              </div>
            ) : (
              <button
                onClick={generateInviteCode}
                disabled={loading}
                className="w-full py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {loading ? '生成中...' : '生成邀请码'}
              </button>
            )}

            {error && (
              <p className="mt-3 text-sm text-red-500 text-center">{error}</p>
            )}
          </motion.div>
        ) : (
          /* Parent View - Accept Invite & View Reports */
          <div className="space-y-6">
            {/* Accept Invite */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl p-6 border border-slate-100"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center">
                  <Users className="w-5 h-5 text-emerald-500" />
                </div>
                <div>
                  <h2 className="font-semibold text-slate-900">绑定学生</h2>
                  <p className="text-sm text-slate-500">输入学生的邀请码进行绑定</p>
                </div>
              </div>

              <div className="flex gap-3">
                <input
                  type="text"
                  value={inputCode}
                  onChange={(e) => setInputCode(e.target.value.toUpperCase())}
                  placeholder="输入6位邀请码"
                  className="flex-1 px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-lg tracking-widest text-center uppercase"
                  maxLength={6}
                />
                <button
                  onClick={acceptInvite}
                  disabled={loading}
                  className="px-6 py-2.5 bg-emerald-600 text-white font-semibold rounded-xl hover:bg-emerald-700 transition-colors disabled:opacity-50"
                >
                  {loading ? '绑定中...' : '绑定'}
                </button>
              </div>

              {error && <p className="mt-3 text-sm text-red-500">{error}</p>}
              {success && <p className="mt-3 text-sm text-emerald-500">{success}</p>}
            </motion.div>

            {/* Linked Students */}
            {students.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-white rounded-2xl border border-slate-100"
              >
                <div className="p-6 border-b border-slate-100">
                  <h2 className="font-semibold text-slate-900">已绑定学生</h2>
                </div>
                <div className="divide-y divide-slate-100">
                  {students.map((student) => (
                    <div
                      key={student.userId}
                      className="p-4 hover:bg-slate-50 cursor-pointer transition-colors"
                      onClick={() => loadReport(student.userId)}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-slate-900">{student.name}</div>
                          <div className="text-sm text-slate-500">
                            {student.grade ? `${student.grade}年级` : ''} 
                            {student.school ? ` · ${student.school}` : ''}
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <div className="text-sm text-slate-500">连续学习</div>
                            <div className="font-semibold text-orange-500">{student.streakDays}天</div>
                          </div>
                          <ChevronRight className="w-5 h-5 text-slate-300" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Student Report */}
            {report && selectedStudent && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl p-6 border border-slate-100"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-violet-50 rounded-xl flex items-center justify-center">
                    <Eye className="w-5 h-5 text-violet-500" />
                  </div>
                  <div>
                    <h2 className="font-semibold text-slate-900">学习报告（本周）</h2>
                    <p className="text-sm text-slate-500">
                      {students.find(s => s.userId === selectedStudent)?.name}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <div className="bg-blue-50 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <BookOpen className="w-4 h-4 text-blue-500" />
                      <span className="text-sm text-blue-600">练习题数</span>
                    </div>
                    <div className="text-2xl font-bold text-blue-700">{report.weeklyStats.totalPractice}</div>
                  </div>
                  
                  <div className="bg-emerald-50 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Target className="w-4 h-4 text-emerald-500" />
                      <span className="text-sm text-emerald-600">正确率</span>
                    </div>
                    <div className="text-2xl font-bold text-emerald-700">{report.weeklyStats.accuracy}%</div>
                  </div>
                  
                  <div className="bg-amber-50 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="w-4 h-4 text-amber-500" />
                      <span className="text-sm text-amber-600">平均用时</span>
                    </div>
                    <div className="text-2xl font-bold text-amber-700">{report.weeklyStats.avgTimePerQuestion}秒</div>
                  </div>
                  
                  <div className="bg-purple-50 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Award className="w-4 h-4 text-purple-500" />
                      <span className="text-sm text-purple-600">获得徽章</span>
                    </div>
                    <div className="text-2xl font-bold text-purple-700">{report.badgeCount}</div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-50 rounded-xl p-4">
                    <div className="text-sm text-slate-500 mb-1">总积分</div>
                    <div className="text-xl font-bold text-slate-900">{report.profile.totalPoints}</div>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4">
                    <div className="text-sm text-slate-500 mb-1">错题数</div>
                    <div className="text-xl font-bold text-slate-900">{report.mistakeCount}</div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Empty State */}
            {students.length === 0 && (
              <div className="bg-slate-50 rounded-2xl p-12 text-center">
                <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-700 mb-2">暂未绑定学生</h3>
                <p className="text-slate-500">输入学生的邀请码即可绑定并查看学习情况</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
